// Copyright © SRGTech (Software Resonance Group Inc.). All rights reserved.
// SPDX-License-Identifier: LicenseRef-SRGTech-Proprietary
// See LICENSE and docs/legal/PROPRIETARY_COPYRIGHT.md.

/**
 * University AI usage and cost types (**ADR-0080**).
 *
 * **Money is a string, everywhere, and stays one.** Platform publishes fixed
 * precision decimal strings; nothing here parses them into a `number`. A
 * JavaScript number cannot hold every value `NUMERIC(20, 8)` can, and a cost
 * that is right in the database and wrong on screen is worse than no dashboard.
 *
 * **No type here can carry anything sensitive.** There is no member for a
 * prompt, a response, a credential, a student, a tool argument or a provider
 * error body — the same shape Platform publishes. A screen cannot render what
 * it has no field to receive.
 *
 * **Currencies are separate values, never one.** A summary carries a list of
 * per-currency totals, so there is no field in which a mixed sum could be put.
 */

import { z } from 'zod';

/** Whether an attempt was the primary or a fallback. */
export const attemptRoles = ['primary', 'fallback'] as const;

/** One attempt role. */
export type AttemptRole = (typeof attemptRoles)[number];

/** Outcomes an attempt may have had. */
export const attemptOutcomes = ['succeeded', 'failed', 'cancelled'] as const;

/** One attempt outcome. */
export type AttemptOutcome = (typeof attemptOutcomes)[number];

/** A fixed-precision decimal string, as Platform publishes it. */
const money = z.string().max(40);

export const currencyTotalsSchema = z.object({
  currency: z.string().min(1).max(8),
  estimated_cost: money,
  primary_cost: money,
  fallback_cost: money,
  priced_attempts: z.number().int().min(0),
});

export const usageSummarySchema = z.object({
  start: z.string(),
  end: z.string(),
  attempts: z.number().int().min(0),
  succeeded: z.number().int().min(0),
  failed: z.number().int().min(0),
  cancelled: z.number().int().min(0),
  input_tokens: z.number().int().min(0),
  output_tokens: z.number().int().min(0),
  cached_tokens: z.number().int().min(0),
  reasoning_tokens: z.number().int().min(0),
  unpriced_attempts: z.number().int().min(0),
  totals: z.array(currencyTotalsSchema),
});

export const collegeCostSchema = z.object({
  college_id: z.string().min(1).max(128),
  currency: z.string().min(1).max(16),
  attempts: z.number().int().min(0),
  estimated_cost: money,
  unpriced_attempts: z.number().int().min(0),
});

export const modelCostSchema = z.object({
  provider_type: z.string().min(1).max(64),
  provider_model_ref: z.string().min(1).max(256),
  currency: z.string().min(1).max(16),
  attempts: z.number().int().min(0),
  succeeded: z.number().int().min(0),
  failed: z.number().int().min(0),
  estimated_cost: money,
  unpriced_attempts: z.number().int().min(0),
});

export const dailyCostSchema = z.object({
  day: z.string().length(10),
  currency: z.string().min(1).max(16),
  attempts: z.number().int().min(0),
  estimated_cost: money,
  unpriced_attempts: z.number().int().min(0),
});

const page = {
  page: z.number().int().min(1),
  page_size: z.number().int().min(1),
  total_items: z.number().int().min(0),
  total_pages: z.number().int().min(0),
  has_next: z.boolean(),
  has_previous: z.boolean(),
};

export const collegeCostPageSchema = z.object({ ...page, items: z.array(collegeCostSchema) });
export const modelCostPageSchema = z.object({ ...page, items: z.array(modelCostSchema) });
export const dailyCostSeriesSchema = z.object({ items: z.array(dailyCostSchema) });

/** Headline figures for one window. */
export type UsageSummary = z.infer<typeof usageSummarySchema>;

/** One currency's totals. */
export type CurrencyTotals = z.infer<typeof currencyTotalsSchema>;

/** One college's spend in one currency. */
export type CollegeCost = z.infer<typeof collegeCostSchema>;

/** One provider and model's spend in one currency. */
export type ModelCost = z.infer<typeof modelCostSchema>;

/** One UTC day's spend in one currency. */
export type DailyCost = z.infer<typeof dailyCostSchema>;

/** A page of college costs. */
export type CollegeCostPage = z.infer<typeof collegeCostPageSchema>;

/** A page of model costs. */
export type ModelCostPage = z.infer<typeof modelCostPageSchema>;

/** The daily series. */
export type DailyCostSeries = z.infer<typeof dailyCostSeriesSchema>;

/**
 * What one dashboard question is asking about.
 *
 * There is no University member: the estate comes from the verified session at
 * the service, and a field here would suggest a browser could choose one.
 */
export interface UsageFilters {
  readonly start?: string | undefined;
  readonly end?: string | undefined;
  readonly college_id?: string | undefined;
  readonly provider_type?: string | undefined;
  readonly provider_model_ref?: string | undefined;
  readonly role?: AttemptRole | undefined;
  readonly outcome?: AttemptOutcome | undefined;
  readonly page?: number | undefined;
  readonly page_size?: number | undefined;
}

/** Reads for the AI usage and cost dashboard. */
export interface AiUsageClient {
  summary(filters?: UsageFilters, signal?: AbortSignal): Promise<UsageSummary>;
  byCollege(filters?: UsageFilters, signal?: AbortSignal): Promise<CollegeCostPage>;
  breakdown(filters?: UsageFilters, signal?: AbortSignal): Promise<ModelCostPage>;
  trend(filters?: UsageFilters, signal?: AbortSignal): Promise<DailyCostSeries>;
  /** The href a download link points at, with the same filters applied. */
  exportHref(filters?: UsageFilters): string;
}

/**
 * Render filters as query parameters, omitting the ones nobody set.
 *
 * @param filters - The dashboard's current filters.
 * @returns Query parameters for the request.
 */
export function usageQuery(filters: UsageFilters | undefined): Record<string, string> {
  const query: Record<string, string> = {};
  if (filters === undefined) {
    return query;
  }
  for (const [name, value] of Object.entries(filters)) {
    if (value !== undefined && value !== null && value !== '') {
      query[name] = String(value);
    }
  }
  return query;
}
